import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import axiosExt from '../axios';
import { AxiosError } from "axios";
import { Customer } from "../types";
//import { validateEmail, validateDomain } from "../utils/validation";
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";

import CustomerDialog from "./CustomerDialog";

//const customerActivities = ["direct", "indirect"];
//const payTypes = ["Pre-Paid", "Post-Paid"];

const CustomerTable: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate()

    const [customers, setCustomers] = useState<Customer[]>([]);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [customerToDelete, setCustomerToDelete] = useState<string | null>(null);
    const [showCustomerDialog, setShowCustomerDialog] = useState(false);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    const [settings, setSettings] = useState<Record<string, string>>({});

    const savedSortKey = "sortCustomerList";
    const savedFilterKey = "filterCustomerList";

    // Get user roles from localStorage
    const roles: string[] = JSON.parse(localStorage.getItem("roles") || "[]");
    const isAdmin = roles.includes("admin");

    const fetchSettings = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get(`${API_BASE_URL}/settings`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            setSettings(response.data);
        } catch (error) {
            console.error("Error fetching settings:", error);
        } 
    };

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        fetchSettings();
        console.log("Fetching customers...");
        fetchCustomers();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchCustomers = async () => {
        //setLoading(true);

        try {
            const token = localStorage.getItem("token");
            /*const response = await axiosExt.get<Customer[]>(`${API_BASE_URL}/customers`);
            setCustomers(response.data);*/

            const response = await fetch(`${API_BASE_URL}/customers`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }

            const data = await response.json();

           setCustomers(data);

        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            //setLoading(false);
        }
    };

    const handleEdit = (customer: Customer) => {
        setSelectedCustomer(customer);
        setShowCustomerDialog(true); 
    };

    const handleDeleteClick = async (id: string) => {
        if (!isAdmin) {
            alert("Only admin users can delete customers.");
            return;
        }

        try {
            setCustomerToDelete(id);
            setConfirmDeleteOpen(true);
            //setCustomers(customers.filter((customer) => customer.id !== id));
        } catch (error) {
            console.error("Error deleting customer:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (!isAdmin || customerToDelete === null) return;

        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("No auth token found");

            await axiosExt.delete(`${API_BASE_URL}/customers/${customerToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setCustomers(customers.filter((customer) => customer.id !== customerToDelete));
        } catch (error: unknown) {
            const axiosError = error as AxiosError;

            const serverMessage =
                axiosError.response?.data && typeof axiosError.response.data === "string"
                    ? axiosError.response.data
                    : axiosError.message;

            alert("Error deleting customer: " + serverMessage);
            console.error("Error deleting customer:", axiosError);
        } finally {
            setConfirmDeleteOpen(false);
            setCustomerToDelete(null);
        }

    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setCustomerToDelete(null);
    };

    const columns: GridColDef[] = [
        { field: "name", headerName: t("Name"), width: 150 },
        { field: "email", headerName: t("Email"), width: 200 },
        { field: "phone", headerName: t("Phone"), width: 100 },
        { field: "webDomain", headerName: t("Web Domain"), width: 150 },
        { field: "accountingCustomerNumber", headerName: t("Accounting #"), width: 100 },
        { field: "taxInvoiceDueDays", headerName: t("Tax Due Days"), width: 75 },
        { field: "serviceInvoiceDueDays", headerName: t("Service Due Days"), width: 75 },
        { field: "serviceInvoiceTaxPercentageFee", headerName: t("Tax Fee %"), width: 75 },
        { field: "assignedToUserId", headerName: t("Assigned To User"), width: 150 },
        { field: "activity", headerName: t("Activity"), width: 100 },
        { field: "payType", headerName: t("Pay Type"), width: 100 },
        { field: "notes", headerName: t("Notes"), width: 150 },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 400,
            renderCell: (params: GridRenderCellParams) => (
                <>

                    <Button
                        variant="contained"
                        onClick={() => handleEdit(params.row as Customer)}
                        style={{ marginRight: "10px" }}
                    >
                        {t("Edit")}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(`/contacts?customerId=${params.row.id}`)}
                        style={{ marginRight: "10px" }}
                    >
                        {t("Contacts")}
                    </Button>

                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() =>
                            navigate(`/customerpricing?customerId=${params.row.id}`)
                        }
                        style={{ marginRight: "10px" }}
                    >
                        Pricing
                    </Button>

                    {isAdmin && (
                        <Button variant="contained" color="error" onClick={() => handleDeleteClick(params.row.id)} style={{ marginRight: "10px" }}>{t("Delete")}</Button>
                    )}
                </>
            ),
        },
    ];

    return (
        <div style={{ height: "100%"}}>
            <div style={{ width: "100%", display:"flex", marginTop:"0px"}}>
                <h2>{t("All Customers")}</h2>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setSelectedCustomer({ id: "", name: "", email: "", phone: "", webDomain: "", accountingCustomerNumber: "", activity: "", payType: "", notes: "",serviceInvoiceTaxPercentageFee:0 });
                        setShowCustomerDialog(true); 
                    }}
                    style={{ marginBottom: "20px", marginTop:"20px", marginLeft:"20px" }}
                >
                    {t("Add New")}
                </Button>
            </div>
            <div style={{ width: "100%" }}>
                <DataGrid rows={customers} columns={columns}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    filterModel={filterModel}
                    onFilterModelChange={handleFilterModelChange}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25, page: 0 },
                        },
                    }}
                    pageSizeOptions={[25, 50, 100, 200]} // Optional: Specify page size options
                />
                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                    <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                    </DialogActions>
                </Dialog>
            </div>

            <CustomerDialog
                open={showCustomerDialog}
                customer={selectedCustomer}
                allowSurvey={
                    parseInt(settings["CustomerSatisfactionSurvey:SurveySendingDays"] ?? "-1", 10) >= 0
                } 
                onClose={() => {
                    setShowCustomerDialog(false);
                    setSelectedCustomer(null);
                }}
                onSaved={fetchCustomers}
            />
        </div>
    );
};

export default CustomerTable;
